import { mdiMessageAlertOutline } from '@mdi/js'
import Icon from '@mdi/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import DrawerDialog from '@/components/drawer'
import { Box } from '@/components/ui/box'
import { Checkbox } from '@/components/ui/checkbox'
import { DrawerBody, DrawerHeader } from '@/components/ui/drawer'
import { Stack } from '@/components/ui/stack'
import { Text } from '@/components/ui/typography'
import useColorModeValue from '@/hooks/useColorModeValue'

import DrawerFooter from './components/drawerFooter'

const ResolutionDrawer = ({
    files,
    isOpen,
    onClose,
    onSetReplacedFiles,
    onSetKeepBoth,
}: {
    files: Array<File>
    isOpen: boolean
    onClose: () => void
    onSetReplacedFiles: (files: Array<File>) => void
    onSetKeepBoth: (files: Array<File>) => void
}) => {
    const fileCheckboxList = new Array(files.length).fill(false)
    const [isSelectAll, setIsSelectAll] = useState<boolean>(false)
    const [selectedItems, setSelectedItems] = useState<Array<boolean>>(fileCheckboxList)
    const { t } = useTranslation()
    const borderColor = useColorModeValue('gray.300', 'gray.700')

    const onSelectAll = () => {
        setIsSelectAll(!isSelectAll)
        const selectAllArray = new Array(files.length).fill(!isSelectAll)
        setSelectedItems(selectAllArray)
    }

    const onDrawerClose = () => {
        const selectAllArray = new Array(files.length).fill(false)
        setSelectedItems(selectAllArray)
        setIsSelectAll(false)
        onClose()
    }

    const onItemChange = (position: number) => {
        let updateState = []
        if (!selectedItems.length) {
            updateState = fileCheckboxList.map((item, index) => (index === position ? !item : item) as boolean)
        } else {
            updateState = selectedItems.map((item, index) => (index === position ? !item : item))
        }

        setSelectedItems(updateState)

        const checkIfAllAreSelected = updateState.length ? updateState.every((item) => item === true) : false
        if (checkIfAllAreSelected) {
            setIsSelectAll(true)
        } else if (isSelectAll) {
            setIsSelectAll(false)
        }
    }

    const getButtonState = () => {
        const checkSelectedItem = selectedItems.every((item) => item === false)
        return checkSelectedItem
    }

    const onReplace = () => {
        const fileToReplace: Array<File> = []

        selectedItems.map((item, index) => {
            if (item) {
                fileToReplace.push(files[index])
            }
        })

        onSetReplacedFiles(fileToReplace)
        const selectAllArray = new Array(files.length).fill(false)
        setSelectedItems(selectAllArray)
        setIsSelectAll(false)
    }

    const onKeep = () => {
        const fileToReplace: Array<File> = []

        selectedItems.map((item, index) => {
            if (item) {
                fileToReplace.push(files[index])
            }
        })

        onSetKeepBoth(fileToReplace)
        const selectAllArray = new Array(files.length).fill(false)
        setSelectedItems(selectAllArray)
        setIsSelectAll(false)
    }

    return (
        <DrawerDialog isOpen={isOpen} onClose={onClose}>
            <DrawerHeader borderBottomWidth="1px" borderColor={borderColor} px="0" py="24px">
                <Box display="flex" alignItems="center">
                    <Icon path={mdiMessageAlertOutline} size={1} />
                    <Text ml="18px">{t('documents.resolutionDrawer.fileExist')}</Text>
                </Box>
            </DrawerHeader>
            <DrawerBody px="0" py="24px">
                <Checkbox
                    ml="3px"
                    variant="gsk"
                    alignItems="top"
                    onCheckedChange={() => onSelectAll()}
                    checked={isSelectAll}
                    className="consentChekbox"
                >
                    <Text fontSize="14px" letterSpacing="0.25px">
                        {t('documents.resolutionDrawer.selectAll')}
                    </Text>
                </Checkbox>
                <Stack mt={1} ml="3px" gap={1}>
                    {files.map((file: File, index: number) => {
                        return (
                            <Checkbox
                                variant="gsk"
                                alignItems="top"
                                onCheckedChange={() => onItemChange(index)}
                                checked={selectedItems[index]}
                                className="consentChekbox"
                                key={file.name + index}
                            >
                                <Text as="span" fontSize="14px" letterSpacing="0.25px" fontWeight="normal">
                                    {file.name}
                                </Text>
                            </Checkbox>
                        )
                    })}
                </Stack>
            </DrawerBody>
            <DrawerFooter onReplace={onReplace} onKeep={onKeep} onClose={onDrawerClose} shouldDisable={getButtonState()} />
        </DrawerDialog>
    )
}

export default ResolutionDrawer
